import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Volume2, VolumeX, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = '';

const MusicAutoPlayer = () => {
  const { themeColor } = useTheme();
  const [trackData, setTrackData] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const fetchedRef = useRef(false);

  const extractTrackId = (url) => {
    if (!url) return null;
    const match = url.match(/track\/([a-zA-Z0-9]+)/);
    return match ? match[1] : null;
  };

  const fetchNowPlaying = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/spotify/now-playing`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      if (data.isPlaying) {
        setTrackData(data);
      } else {
        setTrackData(null);
        setShowPlayer(false);
      }
    } catch (_) {
      // network error
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      fetchNowPlaying();
    }
    const interval = setInterval(fetchNowPlaying, 30000);
    return () => clearInterval(interval);
  }, [fetchNowPlaying]);

  if (!trackData) return null;

  const trackId = extractTrackId(trackData.songUrl);

  return (
    <>
      {/* Compact Spotify embed - shown when user clicks play */}
      <AnimatePresence>
        {showPlayer && trackId && (
          <motion.div
            className="fixed bottom-[72px] left-4 sm:left-6 z-50 overflow-hidden shadow-2xl"
            style={{ borderRadius: 12, border: `1px solid rgba(${themeColor.rgb}, 0.2)` }}
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            data-testid="spotify-compact-player"
          >
            <button
              onClick={() => setShowPlayer(false)}
              className="no-shape absolute top-1 right-1 z-10 p-1 rounded-full bg-black/60 text-white/60 hover:text-white transition-colors"
              data-testid="spotify-player-close"
            >
              <X size={12} />
            </button>
            <iframe
              src={`https://open.spotify.com/embed/track/${trackId}?utm_source=generator&theme=0`}
              width="280"
              height="80"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              title="Spotify Player"
              style={{ borderRadius: 12, display: 'block', maxWidth: 'calc(100vw - 2rem)' }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom-left bar */}
      <motion.div
        className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-50 flex items-center gap-2"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        data-testid="music-player-control"
      >
        {/* Toggle button */}
        <motion.button
          onClick={() => setShowPlayer(prev => !prev)}
          className="no-shape relative p-3 rounded-full backdrop-blur-xl border transition-all"
          style={{
            backgroundColor: 'rgba(10,10,10,0.8)',
            borderColor: showPlayer ? `rgba(${themeColor.rgb}, 0.4)` : 'rgba(63,63,70,0.5)',
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          data-testid="music-mute-btn"
        >
          {showPlayer ? (
            <Volume2 size={18} style={{ color: themeColor.primary }} />
          ) : (
            <VolumeX size={18} className="text-zinc-500" />
          )}
          {showPlayer && (
            <motion.div
              className="absolute -top-1 -right-1 w-3 h-3 rounded-full"
              style={{ backgroundColor: themeColor.primary }}
              animate={{ scale: [1, 1.8, 1], opacity: [0.8, 0, 0.8] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          )}
        </motion.button>

        {/* Song info */}
        <motion.div
          className="flex items-center gap-2.5 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl backdrop-blur-xl border border-zinc-800/50 max-w-[180px] sm:max-w-[240px] cursor-pointer"
          style={{ backgroundColor: 'rgba(10,10,10,0.85)' }}
          onClick={() => setShowPlayer(prev => !prev)}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          data-testid="music-song-info"
        >
          {showPlayer ? (
            <div className="flex items-end gap-0.5 h-4 flex-shrink-0">
              {[1, 2, 3].map((bar) => (
                <motion.div
                  key={bar}
                  className="w-1 rounded-full"
                  style={{ backgroundColor: themeColor.primary }}
                  animate={{ height: ['3px', '14px', '5px', '11px', '3px'] }}
                  transition={{ duration: 0.9, repeat: Infinity, delay: bar * 0.12 }}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-end gap-0.5 h-4 flex-shrink-0">
              {[1, 2, 3].map((bar) => (
                <div key={bar} className="w-1 h-1 rounded-full bg-zinc-600" />
              ))}
            </div>
          )}
          <span className="text-sm text-zinc-300 truncate">
            {trackData.title} - {trackData.artist}
          </span>
        </motion.div>
      </motion.div>
    </>
  );
};

export default MusicAutoPlayer;
